<script setup>
import { ref, computed, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import FileTypeIcon from './FileTypeIcon.vue';

const props = defineProps({
  file: {
    type: Object,
    default: null,
  },
  isOpen: {
    type: Boolean,
    default: false,
  },
  currentIndex: {
    type: Number,
    default: 0,
  },
  totalFiles: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits([
  'close',
  'download',
  'delete',
  'rename',
  'move',
  'share',
  'prev',
  'next',
]);

const { t } = useI18n();

const showActionsMenu = ref(false);

const showModal = computed({
  get: () => props.isOpen,
  set: value => {
    if (!value) {
      emit('close');
    }
  },
});

const handleClose = () => {
  emit('close');
};

const handlePrev = () => {
  emit('prev');
};

const handleNext = () => {
  emit('next');
};

// Raccourcis clavier
const handleKeyboard = e => {
  if (!props.isOpen) return;

  if (e.key === 'Escape') {
    e.preventDefault();
    handleClose();
  } else if (e.key === 'ArrowLeft') {
    e.preventDefault();
    handlePrev();
  } else if (e.key === 'ArrowRight') {
    e.preventDefault();
    handleNext();
  } else if (e.key === ' ' || e.code === 'Space') {
    e.preventDefault();
    handleClose();
  }
};

watch(
  () => props.isOpen,
  isOpen => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyboard);
    } else {
      document.removeEventListener('keydown', handleKeyboard);
    }
  }
);

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyboard);
});

const isImage = computed(() => props.file?.file_type === 'image');
const isVideo = computed(() => props.file?.file_type === 'video');
const isPDF = computed(() => props.file?.file_type === 'pdf');

const formatDate = dateString => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
};
</script>

<template>
  <Modal
    v-model:show="showModal"
    :on-close="handleClose"
    size="medium"
    class="drive-preview-modal"
  >
    <template #header>
      <div class="flex items-center justify-center w-full">
        <!-- Navigation hints seulement -->
        <div class="flex items-center gap-3 text-xs text-n-slate-10">
          <div class="flex items-center gap-1.5">
            <kbd
              class="px-1.5 py-0.5 rounded text-[10px] border border-n-weak bg-n-alpha-1 text-n-slate-11 font-mono"
            >
              <span class="i-lucide-arrow-left size-2.5" />
            </kbd>
            <kbd
              class="px-1.5 py-0.5 rounded text-[10px] border border-n-weak bg-n-alpha-1 text-n-slate-11 font-mono"
            >
              <span class="i-lucide-arrow-right size-2.5" />
            </kbd>
          </div>
          <kbd
            class="px-1.5 py-0.5 rounded text-[10px] border border-n-weak bg-n-alpha-1 text-n-slate-11 font-mono"
          >
            {{ 'esc' }}
          </kbd>
        </div>
      </div>
    </template>

    <!-- Content -->
    <div class="flex flex-col gap-4">
      <!-- Preview principal -->
      <div
        class="relative bg-n-alpha-1 rounded-xl overflow-hidden border border-n-weak h-[600px] flex items-center justify-center p-8 transition-all duration-200"
      >
        <!-- Image preview -->
        <img
          v-if="isImage"
          :src="
            file?.preview_url || file?.file_url || file?.share_url || file?.url
          "
          :alt="file?.name"
          class="max-w-full max-h-full object-contain rounded-lg shadow-lg transition-all duration-200"
        />

        <!-- Video preview -->
        <video
          v-else-if="isVideo"
          :src="
            file?.preview_url || file?.file_url || file?.share_url || file?.url
          "
          controls
          class="max-w-full max-h-full rounded-lg shadow-lg"
        >
          {{ t('DRIVE.PREVIEW.VIDEO_NOT_SUPPORTED') }}
        </video>

        <!-- PDF preview -->
        <iframe
          v-else-if="isPDF"
          :src="
            file?.preview_url || file?.file_url || file?.share_url || file?.url
          "
          class="w-full h-full border-0 rounded-lg shadow-lg"
        />

        <!-- No preview -->
        <div
          v-else
          class="flex flex-col items-center justify-center p-12 w-full"
        >
          <div class="mb-6 w-28 h-28 flex items-center justify-center mx-auto">
            <FileTypeIcon v-if="file" :file="file" size="lg" />
          </div>
          <p class="text-base font-medium text-n-slate-12 mb-2 text-center">
            {{ t('DRIVE.PREVIEW.NO_PREVIEW') }}
          </p>
          <p class="text-sm text-n-slate-10 mb-6 text-center max-w-sm">
            {{ t('DRIVE.PREVIEW.DOWNLOAD_TO_VIEW') }}
          </p>
          <Button
            :label="t('DRIVE.DOWNLOAD')"
            icon="i-lucide-download"
            @click="emit('download', file)"
          />
        </div>
      </div>

      <!-- Footer professionnel raffiné -->
      <div class="pt-5">
        <!-- Section info fichier -->
        <div
          class="flex flex-col gap-4 p-4 rounded-2xl bg-gradient-to-b from-n-alpha-1/50 to-n-alpha-1 border border-n-weak/50 backdrop-blur-sm"
        >
          <!-- Ligne principale: Nom + Badge type -->
          <div class="flex items-start justify-between gap-4">
            <div class="flex flex-col gap-1.5 min-w-0 flex-1">
              <h3
                class="text-base font-semibold text-n-slate-12 truncate leading-tight"
              >
                {{ file?.name }}
              </h3>
              <div class="flex items-center gap-2.5 text-xs text-n-slate-10">
                <span class="inline-flex items-center gap-1">
                  <span class="i-lucide-hard-drive size-3 opacity-70" />
                  <span class="tabular-nums font-medium">{{
                    file?.human_file_size
                  }}</span>
                </span>
                <span class="opacity-40">•</span>
                <span class="inline-flex items-center gap-1">
                  <span class="i-lucide-file-type size-3 opacity-70" />
                  <span class="uppercase font-semibold tracking-wide">{{
                    file?.extension
                  }}</span>
                </span>
              </div>
            </div>

            <!-- Badge type raffiné -->
            <div
              class="px-3 py-1.5 rounded-lg bg-n-alpha-2/50 border border-n-weak/50 text-[10px] font-bold uppercase tracking-wider text-n-slate-11 whitespace-nowrap flex-shrink-0 backdrop-blur-sm"
            >
              {{ file?.file_type || 'file' }}
            </div>
          </div>

          <!-- Ligne métadonnées -->
          <div class="flex items-center gap-4 pt-3 border-t border-n-weak/30">
            <div class="flex items-center gap-2 text-xs text-n-slate-10">
              <div class="p-1 rounded bg-n-alpha-1">
                <span class="i-lucide-user size-3 text-n-slate-9" />
              </div>
              <span class="font-medium">{{ file?.user?.name }}</span>
            </div>
            <span class="text-n-slate-9 opacity-40">•</span>
            <div class="flex items-center gap-2 text-xs text-n-slate-10">
              <div class="p-1 rounded bg-n-alpha-1">
                <span class="i-lucide-calendar size-3 text-n-slate-9" />
              </div>
              <span class="tabular-nums">{{
                formatDate(file?.created_at)
              }}</span>
            </div>
          </div>
        </div>

        <!-- Barre d'actions élégante -->
        <div class="flex items-center justify-between gap-3 mt-4">
          <!-- Navigation avec compteur intégré -->
          <div
            class="flex items-center gap-2 px-3 py-2 rounded-xl bg-n-alpha-1/50 border border-n-weak/50"
          >
            <Button
              icon="i-lucide-chevron-left"
              color="slate"
              size="sm"
              variant="ghost"
              :disabled="currentIndex === 0"
              @click="handlePrev"
            />
            <div class="px-2 py-1 rounded-lg bg-n-alpha-1/50">
              <span class="text-xs font-semibold text-n-slate-11 tabular-nums">
                {{ currentIndex + 1 }}
              </span>
              <span class="text-xs text-n-slate-9 mx-1">/</span>
              <span class="text-xs font-medium text-n-slate-10 tabular-nums">
                {{ totalFiles }}
              </span>
            </div>
            <Button
              icon="i-lucide-chevron-right"
              color="slate"
              size="sm"
              variant="ghost"
              :disabled="currentIndex === totalFiles - 1"
              @click="handleNext"
            />
          </div>

          <!-- Actions principales -->
          <div class="flex items-center gap-2">
            <Button
              icon="i-lucide-download"
              :label="t('DRIVE.DOWNLOAD')"
              color="slate"
              size="sm"
              variant="solid"
              @click="emit('download', file)"
            />

            <!-- Menu actions raffiné -->
            <div class="relative">
              <Button
                icon="i-lucide-more-vertical"
                color="slate"
                size="sm"
                variant="ghost"
                @click.stop="showActionsMenu = !showActionsMenu"
              />

              <!-- Dropdown premium -->
              <div
                v-if="showActionsMenu"
                class="absolute bottom-full right-0 mb-2 p-1.5 rounded-xl shadow-2xl bg-n-alpha-3/80 backdrop-blur-[120px] outline-1 outline outline-n-weak/40 min-w-[190px] z-50"
                @click.stop
              >
                <button
                  class="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium text-n-slate-12 hover:bg-n-brand hover:text-white transition-all duration-200"
                  @click="
                    emit('rename', file);
                    showActionsMenu = false;
                  "
                >
                  <span
                    class="i-lucide-pencil size-4 flex-shrink-0 opacity-80"
                  />
                  <span>{{ t('DRIVE.RENAME') }}</span>
                </button>
                <button
                  class="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium text-n-slate-12 hover:bg-n-brand hover:text-white transition-all duration-200"
                  @click="
                    emit('move', file);
                    showActionsMenu = false;
                  "
                >
                  <span
                    class="i-lucide-folder-input size-4 flex-shrink-0 opacity-80"
                  />
                  <span>{{ t('DRIVE.MOVE') }}</span>
                </button>
                <button
                  class="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium text-n-slate-12 hover:bg-n-brand hover:text-white transition-all duration-200"
                  @click="
                    emit('share', file);
                    showActionsMenu = false;
                  "
                >
                  <span
                    class="flex-shrink-0 size-4 opacity-80"
                    :class="
                      file.share_enabled
                        ? 'i-lucide-check-circle'
                        : 'i-lucide-link'
                    "
                  />
                  <span>{{ t('DRIVE.SHARE') }}</span>
                </button>
                <div class="h-px bg-n-weak/50 my-1.5 mx-2" />
                <button
                  class="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-medium text-red-11 hover:bg-red-9 hover:text-white transition-all duration-200"
                  @click="
                    emit('delete', file);
                    showActionsMenu = false;
                  "
                >
                  <span
                    class="i-lucide-trash-2 size-4 flex-shrink-0 opacity-80"
                  />
                  <span>{{ t('DRIVE.DELETE') }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style lang="scss">
.drive-preview-modal .modal-container {
  @apply max-w-[90%] w-[75rem] rounded-xl;
  padding: 2rem !important;
  height: auto;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.drive-preview-modal .modal-content-wrap {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.drive-preview-modal .content {
  @apply p-0;
  padding: 0 !important;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.drive-preview-modal .header {
  @apply border-b-0 p-0;
  padding: 0 !important;
  margin-bottom: 1rem;
  flex-shrink: 0;
}
</style>
